import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Typography,
  withStyles,
  CircularProgress,
} from "@material-ui/core";
import Autocomplete from "@material-ui/lab/Autocomplete";
import products from "../../service/products";
import { getTenant } from "../../libs/tenant";
import { parseDecimal } from "../../libs/format";
import DialogError from "../alertas/DialogError";

const CURRENCIES = ["BRL", "USD", "PYG"];

// Adiciona um produto ao grupo: SELECIONA um existente da empresa (reuso) OU cria
// um novo. Props: group, priceTables[], close(), save().
class AddProductDialog extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      loadingList: true,
      allProducts: [],
      selected: null, // produto existente escolhido
      typedName: "", // nome digitado quando é produto novo
      // atributos (produto novo)
      category: "",
      presentation: "",
      unit_of_measure: "",
      package_quantity: "",
      currency: "BRL",
      code: "",
      priceByTable: {},
      saving: false,
      showError: false,
      error: null,
    };
  }

  componentDidMount() {
    products
      .listAllProducts(getTenant())
      .then((allProducts) => this.setState({ allProducts, loadingList: false }))
      .catch(() => this.setState({ loadingList: false }));
  }

  // Ao escolher um produto existente, pré-preenche os preços nas tabelas deste grupo.
  handleSelect = (event, value) => {
    if (value && typeof value === "object") {
      const priceByTable = {};
      (value.prices || []).forEach((p) => {
        priceByTable[p.price_table_id] = p.price == null ? "" : String(p.price);
      });
      this.setState({ selected: value, typedName: value.name, priceByTable });
    } else {
      this.setState({ selected: null, typedName: value || "", priceByTable: {} });
    }
  };

  handlePriceChange = (tableId) => (event) => {
    this.setState({
      priceByTable: { ...this.state.priceByTable, [tableId]: event.target.value },
    });
  };

  handleChange = (name) => (event) => this.setState({ [name]: event.target.value });

  buildPrices = () => {
    const prices = [];
    (this.props.priceTables || []).forEach((table) => {
      const value = parseDecimal(this.state.priceByTable[table.id]);
      if (value !== null) prices.push({ price_table_id: table.id, price: value });
    });
    return prices;
  };

  fail = (title, err) => {
    this.setState({
      saving: false,
      showError: true,
      error: {
        title,
        message:
          (err && err.data && err.data.message) ||
          "Não foi possível concluir a operação.",
      },
    });
  };

  save = () => {
    const prices = this.buildPrices();
    this.setState({ saving: true });

    if (this.state.selected) {
      // Reusa produto existente: vincula ao grupo (+ preços opcionais).
      products
        .attachProductToGroup(this.props.group.id, {
          product_id: this.state.selected.id,
          prices: prices.length ? prices : undefined,
        })
        .then(() => this.props.save())
        .catch((err) => this.fail("Erro ao vincular produto", err));
      return;
    }

    // Produto novo.
    const name = this.state.typedName.trim();
    if (!name) {
      this.setState({
        saving: false,
        showError: true,
        error: { title: "Nome obrigatório", message: "Informe o nome do produto." },
      });
      return;
    }
    products
      .createProduct({
        product_group_id: this.props.group.id,
        name,
        category: this.state.category || null,
        presentation: this.state.presentation || null,
        unit_of_measure: this.state.unit_of_measure || null,
        package_quantity: parseDecimal(this.state.package_quantity),
        currency: this.state.currency || null,
        code: this.state.code || null,
        prices: prices.length ? prices : undefined,
      })
      .then(() => this.props.save())
      .catch((err) => this.fail("Erro ao criar produto", err));
  };

  render() {
    const { classes, priceTables } = this.props;
    const isExisting = !!this.state.selected;
    const isNew = !isExisting && this.state.typedName.trim() !== "";
    return (
      <Dialog open onClose={() => this.props.close()} maxWidth="md" fullWidth>
        <DialogError
          open={this.state.showError}
          onClose={() => this.setState({ showError: false })}
          title={this.state.error ? this.state.error.title : "Erro"}
          message={this.state.error ? this.state.error.message : ""}
          suggestion={"Verifique os campos e tente novamente."}
        />
        <DialogTitle>Adicionar produto ao catálogo</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" paragraph>
            Selecione um produto já existente na empresa para reutilizá-lo, ou
            digite um nome novo para cadastrá-lo.
          </Typography>

          <Autocomplete
            freeSolo
            options={this.state.allProducts}
            loading={this.state.loadingList}
            getOptionLabel={(option) =>
              typeof option === "string" ? option : option.name
            }
            onChange={this.handleSelect}
            onInputChange={(event, value, reason) => {
              if (reason === "input") {
                this.setState({ selected: null, typedName: value });
              }
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Produto (buscar existente ou digitar novo)"
                variant="outlined"
              />
            )}
          />

          {isExisting && (
            <div className={classes.info}>
              <Typography variant="subtitle2">
                Reutilizando: <b>{this.state.selected.name}</b>
                {this.state.selected.currency
                  ? ` (${this.state.selected.currency})`
                  : ""}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                O estoque é único e compartilhado com os demais catálogos deste
                produto.
              </Typography>
            </div>
          )}

          {isNew && (
            <div className={classes.grid}>
              <TextField
                label="Categoria"
                value={this.state.category}
                onChange={this.handleChange("category")}
              />
              <TextField
                label="Apresentação"
                value={this.state.presentation}
                onChange={this.handleChange("presentation")}
              />
              <TextField
                label="Unidade"
                value={this.state.unit_of_measure}
                onChange={this.handleChange("unit_of_measure")}
              />
              <TextField
                label="Qtd. por embalagem"
                type="number"
                value={this.state.package_quantity}
                onChange={this.handleChange("package_quantity")}
              />
              <FormControl>
                <InputLabel id="add-currency-label">Moeda</InputLabel>
                <Select
                  labelId="add-currency-label"
                  value={this.state.currency}
                  onChange={this.handleChange("currency")}
                >
                  {CURRENCIES.map((c) => (
                    <MenuItem key={c} value={c}>
                      {c}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                label="Código (SKU)"
                value={this.state.code}
                onChange={this.handleChange("code")}
              />
            </div>
          )}

          {(isExisting || isNew) && priceTables && priceTables.length > 0 && (
            <>
              <Typography variant="subtitle1" className={classes.sectionTitle}>
                Preços por tabela
              </Typography>
              <div className={classes.grid}>
                {priceTables.map((table) => (
                  <TextField
                    key={table.id}
                    label={table.name}
                    type="number"
                    value={
                      this.state.priceByTable[table.id] == null
                        ? ""
                        : this.state.priceByTable[table.id]
                    }
                    onChange={this.handlePriceChange(table.id)}
                  />
                ))}
              </div>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => this.props.close()} color="primary">
            Cancelar
          </Button>
          <Button
            onClick={this.save}
            color="primary"
            variant="contained"
            disabled={this.state.saving || (!isExisting && !isNew)}
          >
            {this.state.saving && (
              <CircularProgress size={20} style={{ marginRight: 8 }} />
            )}
            {isExisting ? "Vincular ao catálogo" : "Criar e adicionar"}
          </Button>
        </DialogActions>
      </Dialog>
    );
  }
}

const styles = (theme) => ({
  info: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(1.5),
    background: "#e9f1ff",
    borderRadius: 6,
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))",
    gap: theme.spacing(2),
    marginTop: theme.spacing(2),
  },
  sectionTitle: {
    marginTop: theme.spacing(3),
    fontWeight: "bold",
  },
});

export default withStyles(styles)(AddProductDialog);
